import { useEffect, useState } from 'react'
import { t } from '../i18n.ts'
import { enablePush, pushAvailability, refreshSubscription, type PushAvailability } from '../push/subscribe.ts'
import { useAuth } from '../store/AuthProvider.tsx'
import './NotificationSetup.css'

/**
 * Turns notifications on, and explains itself when it cannot.
 *
 * Every state here is a real one someone will hit. The iOS install prerequisite in
 * particular is not an edge case: it is the default state for every iPhone user who
 * follows a link, and offering them an "allow notifications" button that silently
 * does nothing is the worst version of this component.
 */
export function NotificationSetup() {
  const { cloudEnabled, user, vapidPublicKey, getIdToken } = useAuth()
  const [availability, setAvailability] = useState<PushAvailability>('unsupported')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setAvailability(pushAvailability())
  }, [user])

  // Re-upload the current subscription on every app open. iOS never fires
  // `pushsubscriptionchange`, so this is the only thing keeping the server's copy
  // from going stale.
  useEffect(() => {
    if (user && vapidPublicKey) void refreshSubscription(getIdToken)
  }, [user, vapidPublicKey, getIdToken])

  // Nothing to offer without a cloud project, without a signed-in user, or without
  // somewhere to send the subscription.
  if (!cloudEnabled || !user || !vapidPublicKey) return null
  if (availability === 'granted' || availability === 'unsupported') return null

  if (availability === 'needs-install') {
    return (
      <p className="notif notif--info">
        <strong>{t.notifications.needsInstallTitle}</strong> {t.notifications.needsInstallBody}
      </p>
    )
  }

  if (availability === 'denied') {
    // A denied permission cannot be re-requested from the web at all, so this never
    // offers a button that would do nothing.
    return (
      <p className="notif notif--info">
        <strong>{t.notifications.deniedTitle}</strong> {t.notifications.deniedBody}
      </p>
    )
  }

  return (
    <div className="notif">
      <p className="notif__text">{t.notifications.offer}</p>
      <button
        type="button"
        className="notif__button"
        disabled={busy}
        onClick={() => {
          setBusy(true)
          setFailed(false)
          // Permission must be requested from inside this gesture — asking on load
          // returns 'default' on iOS with no prompt and no error.
          void enablePush({ vapidPublicKey, getIdToken })
            .then((result) => {
              setAvailability(pushAvailability())
              if (!result.ok) setFailed(true)
            })
            .finally(() => setBusy(false))
        }}
      >
        {busy ? t.notifications.enabling : t.notifications.enable}
      </button>
      {failed && <p className="notif__error">{t.notifications.failed}</p>}
    </div>
  )
}
